import { Input, type InputProps } from "@ml/ui";
import { useEffect, useState } from "react";

export interface NumberInputProps extends Omit<InputProps, "value" | "onChange" | "type" | "min" | "max"> {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  /** Integers only (default). */
  integer?: boolean;
}

/**
 * Number field that lets people clear and retype freely, then clamps to
 * [min, max] on blur. `onChange` only ever receives valid numbers.
 */
export function NumberInput({ value, onChange, min = 0, max, integer = true, onBlur, onFocus, ...rest }: NumberInputProps) {
  const [text, setText] = useState(String(value));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setText(String(value));
  }, [value, focused]);

  function clamp(n: number) {
    let v = integer ? Math.round(n) : n;
    if (v < min) v = min;
    if (max !== undefined && v > max) v = max;
    return v;
  }

  return (
    <Input
      {...rest}
      type="text"
      inputMode={integer ? "numeric" : "decimal"}
      value={text}
      onFocus={(e) => {
        setFocused(true);
        onFocus?.(e);
      }}
      onChange={(e) => {
        const raw = e.target.value.replace(integer ? /[^\d]/g : /[^\d.]/g, "");
        setText(raw);
        if (raw === "") return;
        const n = Number(raw);
        if (Number.isFinite(n)) onChange(n);
      }}
      onBlur={(e) => {
        setFocused(false);
        const n = text === "" ? min : Number(text);
        const v = clamp(Number.isFinite(n) ? n : min);
        setText(String(v));
        if (v !== value) onChange(v);
        onBlur?.(e);
      }}
    />
  );
}
